import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, topics } from "@/mocks/topics";
import FilterBar from "./components/FilterBar";

export default function TopicList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") ?? "全部";
  const initialKeyword = searchParams.get("q") ?? "";

  const [category, setCategory] = useState(
    categories.includes(initialCategory) ? initialCategory : "全部",
  );
  const [keyword, setKeyword] = useState(initialKeyword);
  const [sort, setSort] = useState("heat");

  const syncParams = (nextCategory: string, nextKeyword: string) => {
    const params: Record<string, string> = {};
    if (nextCategory !== "全部") params.category = nextCategory;
    if (nextKeyword) params.q = nextKeyword;
    setSearchParams(params, { replace: true });
  };

  const handleCategory = (value: string) => {
    setCategory(value);
    syncParams(value, keyword);
  };

  const handleKeyword = (value: string) => {
    setKeyword(value);
    syncParams(category, value);
  };

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const result = topics.filter((t) => {
      const matchCategory = category === "全部" || t.category === category;
      const matchKeyword =
        !kw ||
        t.title.toLowerCase().includes(kw) ||
        t.summary.toLowerCase().includes(kw) ||
        t.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
    return result.sort((a, b) =>
      sort === "heat" ? b.heat - a.heat : a.date < b.date ? 1 : -1,
    );
  }, [category, keyword, sort]);

  const pageTitle = keyword
    ? `「${keyword}」的搜索结果 - 吃瓜观测站`
    : category !== "全部"
      ? `${category}类网红大瓜事件合集 - 吃瓜观测站`
      : "网红大瓜合集 - 多方说法与回应归档";

  usePageMeta({
    title: pageTitle,
    description:
      "浏览吃瓜观测站已归档的网红大瓜事件，按带货风波、直播争议、情感纠葛等类型筛选，支持关键词搜索与热度排序，每条事件都并列出多方说法与当事人回应。",
    keywords: "网红大瓜合集,网红大瓜多方爆料,网红大瓜当事人回应,网红大瓜各方说法,网红大瓜对质现场",
    canonical: "https://ytrongxiang.cn/list",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "网红大瓜合集",
      inLanguage: "zh-CN",
      description: "已归档的网红大瓜事件合集，按类型筛选与关键词搜索。",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: filtered.length,
        itemListElement: filtered.map((t, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: t.title,
          url: `https://ytrongxiang.cn/topic/${t.id}`,
        })),
      },
    },
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "大瓜合集" },
              ]}
            />
            <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              网红大瓜合集
            </h1>
            <p className="mt-3 max-w-3xl font-label text-sm leading-relaxed text-foreground-600 md:text-base">
              这里收录已归档的网红大瓜事件。每一条都同时呈现爆料方、当事人、平台方与旁观者的公开说法，标注信息来源数量与完整度，方便你自己对照判断谁在含糊、谁在转移话题。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-8 md:py-10">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <FilterBar
              active={category}
              keyword={keyword}
              sort={sort}
              onCategory={handleCategory}
              onKeyword={handleKeyword}
              onSort={setSort}
            />

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <p className="font-label text-sm text-foreground-600">
                共 <strong className="text-foreground-950">{filtered.length}</strong> 个事件
                {category !== "全部" && ` · 分类「${category}」`}
                {keyword && ` · 关键词「${keyword}」`}
              </p>
              {(category !== "全部" || keyword) && (
                <button
                  type="button"
                  onClick={() => {
                    setCategory("全部");
                    setKeyword("");
                    setSearchParams({}, { replace: true });
                  }}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-close-line"></i>
                  清除筛选
                </button>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} />
                ))}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的事件
                </h2>
                <p className="mt-2 font-label text-sm text-foreground-600">
                  换个关键词试试，或者先浏览全部归档。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setCategory("全部");
                    setKeyword("");
                    setSearchParams({}, { replace: true });
                  }}
                  className="mt-5 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line"></i>
                  查看全部事件
                </button>
              </div>
            )}

            <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-6 sm:flex-row sm:items-center">
              <p className="font-label text-sm text-foreground-700">
                <strong className="text-foreground-950">没找到想看的？</strong>
                {" "}
                把线索发给我们，核实后会归档成新的对比页面。
              </p>
              <Link
                to="/about#contact"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line"></i>
                提交线索
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}